import { Dispatch, memo, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import IconButton from '@material-ui/core/IconButton';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import WarningIcon from '@material-ui/icons/Warning';
import useGetTranslatedAttribute from 'hooks/useGetTranslatedAttribute';
import { getFrequencyMonths } from 'pages/plasticAssessment/tabs/KpiTab/utils';
import { ReduxState } from 'redux/types';
import Kpi from 'types/Kpi';
import { useDialog } from 'utils/dialog';

import ApproveButton from '../../../ApproveButton/ApproveButton';
import KpiNotesCell from '../../../KpiNotesCell/KpiNotesCell';
import KpiValueCell from '../../../KpiValueCell/KpiValueCell';
import { Props as TableProps } from '../../KpiTable';
import { KpiType } from '../../types';
import { formatSavedCo2, getKpiType } from '../../utils';
import ApprovalStatusChip from './components/ApprovalStatusChip/ApprovalStatusChip';
import { ActionTableCell, PaddingTableCell, StyledTableCell } from './styled';

interface Props extends Pick<TableProps, 'companyId' | 'assessmentId' | 'getKpiNote' | 'setKpiNote' | 'getDataForMonth' | 'setDataForMonth' | 'setDirtyCell' | 'months' | 'editDisabled'> {
    kpi: Kpi;
    index: number;
    getCellId: (kpiIdx: number, monthIdx: number | null) => number;
    minimumApproveButtonWidth: number;
    setMinimumApproveButtonWidth: Dispatch<SetStateAction<number>>;
    setShouldFocusCellId: Dispatch<SetStateAction<number | null>>;
    shouldFocusCellId: number | null;
    firstColumnStyle: React.CSSProperties;
    secondColumnStyle: React.CSSProperties;
}

const ROW_BACKGROUNDS = {
    [KpiType.PA]: '#edf7ed',
    [KpiType.Compulsory]: '#e8f4fd',
    [KpiType.Additional]: '#fff4e5'
};

const KpiRow = (props: Props) => {
    const { kpi, index, getCellId, minimumApproveButtonWidth, setShouldFocusCellId, shouldFocusCellId, setMinimumApproveButtonWidth, companyId, assessmentId, getKpiNote, setKpiNote, getDataForMonth, setDataForMonth, setDirtyCell, months, editDisabled, firstColumnStyle, secondColumnStyle } = props;

    const { type: userType } = useSelector((state: ReduxState) => state.user);

    const { t } = useTranslation();

    const getTranslatedAttribute = useGetTranslatedAttribute();

    const type = getKpiType(kpi);
    const style = { backgroundColor: ROW_BACKGROUNDS[type] };

    const [openDialog, closeDialog] = useDialog();

    return (
        <TableRow key={kpi.id} style={{ backgroundColor: kpi.isCo2SavedNegative ? '#fdecea' : undefined }}>
            <TableCell style={{ ...firstColumnStyle, ...style }}>
                {index + 1}

                {kpi.isCo2SavedNegative && (
                    <IconButton style={{ padding: 0, color: '#611a15', marginTop: '6px' }} onClick={() => {
                        openDialog({
                            title: t('dialogs.kpi_negative_co2_reduction_error.title'),
                            message: t('dialogs.kpi_negative_co2_reduction_error.message'),
                            actions: [
                                {
                                    text: t('dialogs.generic.close'),
                                    color: 'secondary',
                                    action: closeDialog
                                }
                            ]
                        });
                    }}>
                        <WarningIcon />
                    </IconButton>
                )}
            </TableCell>
            <StyledTableCell style={{ ...secondColumnStyle, ...style }}>{kpi.compulsoryKpi ? getTranslatedAttribute(kpi.compulsoryKpi, 'description') : kpi.description}</StyledTableCell>
            <TableCell style={style}>{kpi.items[0]?.quantityInStock ?? ''}</TableCell>
            <KpiNotesCell
                id={getCellId(index, null)}
                index={index}
                kpi={kpi}
                isCustomer={userType === 'customer'}
                getKpiNote={getKpiNote}
                setKpiNote={setKpiNote}
                shouldFocusMe={shouldFocusCellId === getCellId(index, null)}
                setShouldFocusCellId={setShouldFocusCellId}
                setDirtyCell={setDirtyCell}
                editDisabled={editDisabled}
            />

            {months.map((month, idx: number) => {
                if ((month.month - 1) % getFrequencyMonths(kpi.monitoringFrequency) !== 0) {
                    if (idx === 0) {
                        return (
                            <PaddingTableCell colSpan={month.month % getFrequencyMonths(kpi.monitoringFrequency)} />
                        );
                    } else {
                        return null;
                    }
                }

                if (months.length < (idx + getFrequencyMonths(kpi.monitoringFrequency))) {
                    return (
                        <PaddingTableCell colSpan={months.length - idx} />
                    );
                }

                return (
                    <KpiValueCell
                        id={getCellId(index, idx)}
                        month={month}
                        kpi={kpi}
                        isCustomer={userType === 'customer'}
                        getDataForMonth={getDataForMonth}
                        setDataForMonth={setDataForMonth}
                        shouldFocusMe={shouldFocusCellId === getCellId(index, idx)}
                        setShouldFocusCellId={setShouldFocusCellId}
                        setDirtyCell={setDirtyCell}
                        editDisabled={editDisabled}
                        key={idx}
                    />
                );
            })}

            <StyledTableCell style={style}>{kpi.averageUsage}</StyledTableCell>
            <StyledTableCell style={style}>{kpi.lastMonthUsage}</StyledTableCell>
            <StyledTableCell style={style}>{kpi?.items[0]?.reductionPlans[0]?.certificationYear ?? ''}</StyledTableCell>

            {['admin', 'auditor'].includes(userType) && (
                <>
                    <StyledTableCell style={style}>
                        {kpi.type !== 'compulsory' && formatSavedCo2(kpi)}
                    </StyledTableCell>

                    {userType === 'admin' && (
                        <ActionTableCell style={{ ...style, textAlign: 'left' }}>
                            {kpi.type !== 'compulsory' && <ApprovalStatusChip approved={kpi.approved} />}
                        </ActionTableCell>
                    )}
                </>
            )}

            {userType === 'auditor' && (
                <ActionTableCell style={{ ...style, whiteSpace: 'nowrap' }}>
                    {kpi.type !== 'compulsory' && (
                        <ApproveButton
                            id={kpi.id}
                            companyId={companyId}
                            assessmentId={assessmentId}
                            isApproved={kpi.approved}
                            minimumWidth={minimumApproveButtonWidth}
                            setMinimumWidth={setMinimumApproveButtonWidth} />
                    )}
                </ActionTableCell>
            )}
        </TableRow>
    );
};

export default memo(KpiRow);